import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardTitleStore } from 'state/store/chat/use-dashboard-title-store';
import type { Message } from '../types/chat';
import {
  isDashboardAttachment,
  getAttachmentDisplayName,
  getDashboardVersionOrdinals,
} from '../utils/assistant-file-preview';
import { dashboardTitle } from '../utils/dashboard-document';

type TitledFile = {
  fileId: string;
  fileName: string;
  extension?: string;
  /** Stored by the backend for saved dashboard versions; older messages omit it. */
  title?: string | null;
  version?: number | null;
};

/**
 * Title of a `.dashboard` file: the title stored with the version, else read from `content`
 * when the caller already loaded it (and shared with other views of the same file), else the
 * title a view read earlier. Empty when unknown; never loads the file just for a label.
 */
export const useDashboardTitle = (file: TitledFile | null, content?: string) => {
  const fileId = file?.fileId ?? '';
  const isDashboard = file ? isDashboardAttachment(file.fileName, file.extension) : false;
  const parsed = useMemo(
    () => (isDashboard && content ? dashboardTitle(content) : null),
    [isDashboard, content]
  );
  const known = useDashboardTitleStore((state) => state.titles[fileId]);
  const setTitle = useDashboardTitleStore((state) => state.setTitle);

  useEffect(() => {
    if (fileId && parsed !== null) setTitle(fileId, parsed);
  }, [fileId, parsed, setTitle]);

  const stored = isDashboard ? file?.title?.trim() : '';
  return stored || parsed || known || '';
};

/**
 * Version number of a `.dashboard` file: the stored one, else its ordinal among the
 * conversation's agent dashboards in the loaded messages. Null when unknown.
 */
export const useDashboardVersion = (file: TitledFile | null): number | null => {
  const ordinal = useDashboardTitleStore((state) => state.versions[file?.fileId ?? '']);
  if (!file || !isDashboardAttachment(file.fileName, file.extension)) return null;
  return file.version ?? ordinal ?? null;
};

/** Records the version ordinals of the loaded messages for the labels above. */
export const useSyncDashboardVersions = (messages: Message[]) => {
  const setVersions = useDashboardTitleStore((state) => state.setVersions);
  const ordinals = useMemo(() => getDashboardVersionOrdinals(messages), [messages]);

  useEffect(() => {
    setVersions(ordinals);
  }, [ordinals, setVersions]);
};

/** `{title} · v{version}`, or just the title when the version is unknown. */
export const withDashboardVersion = (title: string, version: number | null) =>
  version ? `${title} · v${version}` : title;

export const useUntitledDashboardLabel = () => {
  const { t } = useTranslation();
  return t('UNTITLED_DASHBOARD', { defaultValue: 'Untitled dashboard' });
};

/** Name shown for an attachment: the versioned dashboard title for dashboards, else the file name. */
export const useAttachmentDisplayName = (file: TitledFile | null, content?: string) => {
  const title = useDashboardTitle(file, content);
  const version = useDashboardVersion(file);
  const untitled = useUntitledDashboardLabel();
  if (!file) return '';
  if (!isDashboardAttachment(file.fileName, file.extension)) {
    return getAttachmentDisplayName(file.fileName, file.extension);
  }
  return withDashboardVersion(title || untitled, version);
};
